import { CNS_API_BASE } from "@/config";
import type {
  CNSShareEntityType,
  CNSShareRequest,
  CNSShareRole,
  CNSShareListResponse,
  CNSShare,
} from "@/models/cns";
import { throwOnProblemResponse } from "@/types/problem-detail";

const SHARE_URL = `${CNS_API_BASE}/share`;
const SHARES_CURRENT_USER_URL = `${CNS_API_BASE}/shares/users/current-user`;

/**
 * Share a project, conversation, or workspace with a user.
 * Target is the user's email (users do not know public IDs). For project/conversation pass role viewer or editor; for workspace role is optional (member).
 */
export async function share(
  entityType: CNSShareEntityType,
  entityPublicId: string,
  targetEmail: string,
  role?: CNSShareRole
): Promise<void> {
  const body: CNSShareRequest = {
    entityType,
    entityPublicId,
    email: targetEmail,
  };
  if (role) body.role = role;
  const res = await fetch(SHARE_URL, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) await throwOnProblemResponse(res, "Failed to share");
}

/**
 * List people with access to an entity (owner for workspace, members from share records).
 * Uses GET /share?entityType=...&entityPublicId=...
 */
export async function listShares(
  entityType: CNSShareEntityType,
  entityPublicId: string
): Promise<CNSShareListResponse> {
  const params = new URLSearchParams({ entityType, entityPublicId });
  const res = await fetch(`${SHARE_URL}?${params}`, { credentials: "include" });
  if (!res.ok) await throwOnProblemResponse(res, "Failed to load people with access");
  return res.json();
}

/**
 * Fetch all shares where the current user is the recipient.
 * Use to derive isShared and role per entity (project, conversation, workspace).
 * GET /shares/users/current-user
 */
export async function fetchSharesForCurrentUser(): Promise<CNSShare[]> {
  const res = await fetch(SHARES_CURRENT_USER_URL, { credentials: "include" });
  if (!res.ok) await throwOnProblemResponse(res, "Failed to load shares");
  return res.json();
}

/**
 * Revoke a share (remove invited member). Uses DELETE /share/{sharePublicId}.
 */
export async function revokeShare(sharePublicId: string): Promise<void> {
  const res = await fetch(`${SHARE_URL}/${encodeURIComponent(sharePublicId)}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok) await throwOnProblemResponse(res, "Failed to remove access");
}
